import { useEffect, useState } from "react";
import type { Section } from "../../domain/index.js";
import { useCompositionStore } from "../../stores/compositionStore.js";
import { Button } from "../../ui/Button.js";

interface Props {
  section: Section;
  /** Read-only when the Timeline is in history time-travel mode. */
  disabled?: boolean;
}

/**
 * "Reinterpretar esta seção" — a descriptor input that reinterprets one section
 * in another genre/era/style. It dispatches a `POST /commands` whose text the
 * LLM maps to `SectionGenreShifted` (set) + `RegenerateSection` (spec §9.3).
 *
 * The active shift is REFLECTED from `section.genreShift` (materialized by the
 * `SectionGenreShifted` event over SSE) — this component never mutates state.
 * A "clear" is a `mode:"clear"` event, also via a command, never a delete.
 */
export function SectionGenreShiftControl({ section, disabled = false }: Props): JSX.Element {
  const projectId = useCompositionStore((s) => s.projectId);
  const shift = section.genreShift;

  const [descriptor, setDescriptor] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset the draft when the materialized shift changes under us (SSE, undo).
  useEffect(() => {
    setDescriptor("");
    setError(null);
  }, [shift?.descriptor, shift?.shiftKind]);

  const canApply = Boolean(projectId) && !disabled && descriptor.trim().length > 0;

  async function dispatch(text: string): Promise<void> {
    if (!projectId) return;
    setBusy(true);
    setError(null);
    try {
      await useCompositionStore.getState().runCommand(text);
      setDescriptor("");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  function onApply(): void {
    if (!canApply) return;
    // The LLM emits `SectionGenreShifted` (set) for this section and chains
    // `RegenerateSection` so the new sound is rendered.
    void dispatch(`transforma a seção "${section.name}" em ${descriptor.trim()}`);
  }

  function onClear(): void {
    if (!projectId || disabled) return;
    // `mode:"clear"` — reverts the section to its base sound. Append-only.
    void dispatch(`remove o estilo aplicado na seção "${section.name}"`);
  }

  return (
    <div className="flex flex-col gap-2 border-t border-border pt-3">
      <div className="flex flex-wrap items-center gap-2">
        <p className="catalog-num">REINTERPRETAR ESTA SEÇÃO</p>
        {shift ? (
          <span
            className="inline-flex items-center gap-1.5 border border-moss px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest2 text-moss"
            aria-label={`Genre-shift ativo: ${shift.descriptor}`}
          >
            <span aria-hidden="true">↻</span>
            {shift.descriptor}
            <button
              type="button"
              onClick={onClear}
              disabled={busy || disabled || !projectId}
              aria-label={`Limpar genre-shift da seção ${section.name}`}
              className="ml-0.5 text-moss hover:text-paper disabled:opacity-40"
            >
              ✕
            </button>
          </span>
        ) : null}
      </div>

      <p className="font-mono text-[10px] leading-relaxed text-ink-dim">
        {shift
          ? "Aplique outro estilo, ou limpe para voltar ao som base da seção."
          : "Descreva um novo gênero, era ou estilo — ex.: synthwave anos 80, versão acústica."}
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={descriptor}
          onChange={(e) => setDescriptor(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onApply();
          }}
          disabled={busy || disabled}
          placeholder="ex.: synthwave anos 80"
          aria-label={`Descritor de reinterpretação para a seção ${section.name}`}
          className="min-w-[16rem] flex-1 border-0 border-b border-border bg-transparent px-0 py-1.5 font-mono text-[12px] text-paper outline-none focus:border-paper disabled:opacity-40"
        />
        <Button
          variant="primary"
          disabled={!canApply || busy}
          onClick={onApply}
          aria-label={`Reinterpretar a seção ${section.name}`}
        >
          {busy ? "Aplicando…" : "Reinterpretar"}
        </Button>
      </div>

      {error ? (
        <p
          role="alert"
          className="border border-vinyl/40 bg-vinyl/5 p-2 font-mono text-[10px] uppercase tracking-widest2 text-vinyl"
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}
